import React, { useRef, useEffect, useCallback, useState } from 'react';
import { levelConfig } from '../../data/portfolioData';

// Sound effects using Web Audio API
const createSound = (frequency, duration, type = 'square') => {
  return () => {
    try {
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioCtx.destination);
      
      oscillator.frequency.value = frequency;
      oscillator.type = type;
      gainNode.gain.setValueAtTime(0.3, audioCtx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + duration);
      
      oscillator.start(audioCtx.currentTime);
      oscillator.stop(audioCtx.currentTime + duration);
    } catch (e) {
      console.log('Audio not supported');
    }
  };
};

const sounds = {
  jump: createSound(400, 0.1),
  coin: createSound(800, 0.15),
  levelUp: createSound(600, 0.3, 'sine'),
  hit: createSound(200, 0.2)
};

// Game constants
const GRAVITY = 0.6;
const FRICTION = 0.85;
const JUMP_STRENGTH = -14;
const MOVE_SPEED = 6;
const PLAYER_WIDTH = 40;
const PLAYER_HEIGHT = 50;

export const GameCanvas = ({ 
  currentLevel, 
  onCollectItem, 
  onLevelComplete,
  onGameOver,
  isPaused,
  isMobile 
}) => {
  const canvasRef = useRef(null);
  const gameStateRef = useRef({
    player: { x: 50, y: 300, vx: 0, vy: 0, onGround: false, facingRight: true },
    coins: [],
    infoBlocks: [],
    npcs: [],
    platforms: [],
    collectedCoins: 0,
    cameraX: 0
  });
  const keysRef = useRef({ left: false, right: false, jump: false });
  const animationRef = useRef(null);
  const [touchControls, setTouchControls] = useState({ left: false, right: false, jump: false });

  // Generate level
  const generateLevel = useCallback((levelNum) => {
    const level = levelConfig[levelNum - 1];
    const platforms = [];
    const coins = [];
    const infoBlocks = [];
    const npcs = [];
    
    // Ground platforms
    for (let i = 0; i < 50; i++) {
      platforms.push({
        x: i * 100,
        y: 450,
        width: 100,
        height: 50,
        color: level.groundColor
      });
    }
    
    // Floating platforms with variety
    const platformPatterns = [
      { x: 200, y: 350, width: 120 },
      { x: 400, y: 300, width: 100 },
      { x: 600, y: 250, width: 150 },
      { x: 850, y: 320, width: 80 },
      { x: 1000, y: 200, width: 120 },
      { x: 1200, y: 280, width: 100 },
      { x: 1400, y: 350, width: 130 },
      { x: 1600, y: 220, width: 90 },
      { x: 1800, y: 300, width: 110 },
      { x: 2000, y: 250, width: 100 },
      { x: 2200, y: 320, width: 120 },
      { x: 2400, y: 180, width: 80 },
      { x: 2600, y: 280, width: 140 },
      { x: 2800, y: 350, width: 100 },
      { x: 3000, y: 220, width: 110 },
    ];
    
    platformPatterns.forEach(p => {
      platforms.push({
        x: p.x,
        y: p.y,
        width: p.width,
        height: 20,
        color: level.groundColor
      });
    });
    
    // Coins
    const coinPositions = [
      { x: 250, y: 310 }, { x: 450, y: 260 }, { x: 650, y: 210 },
      { x: 900, y: 280 }, { x: 1050, y: 160 }, { x: 1250, y: 240 },
      { x: 1450, y: 310 }, { x: 1650, y: 180 }, { x: 1850, y: 260 },
      { x: 2050, y: 210 }, { x: 2250, y: 280 }, { x: 2450, y: 140 },
      { x: 2650, y: 240 }, { x: 2850, y: 310 }, { x: 3050, y: 180 }
    ];
    
    coinPositions.forEach((pos, i) => {
      coins.push({
        id: `coin-${i}`,
        x: pos.x,
        y: pos.y,
        width: 25,
        height: 25,
        collected: false,
        rotation: 0
      });
    });
    
    // Info blocks (unlockable items)
    level.items.forEach((item, i) => {
      infoBlocks.push({
        id: item.id || `info-${i}`,
        x: 400 + i * 600,
        y: 150,
        width: 50,
        height: 50,
        data: item,
        triggered: false,
        bounce: 0
      });
    });
    
    // NPCs
    level.npcFacts.forEach((fact, i) => {
      npcs.push({
        id: `npc-${i}`,
        x: 300 + i * 900,
        y: 400,
        width: 40,
        height: 50,
        fact: fact,
        talked: false,
        bobOffset: Math.random() * Math.PI * 2
      });
    });
    
    return { platforms, coins, infoBlocks, npcs };
  }, []);

  // Initialize level
  useEffect(() => {
    const levelData = generateLevel(currentLevel);
    gameStateRef.current = {
      ...gameStateRef.current,
      ...levelData,
      player: { x: 50, y: 300, vx: 0, vy: 0, onGround: false, facingRight: true },
      collectedCoins: 0,
      cameraX: 0
    };
  }, [currentLevel, generateLevel]);

  // Handle keyboard input
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.code === 'ArrowLeft' || e.code === 'KeyA') keysRef.current.left = true;
      if (e.code === 'ArrowRight' || e.code === 'KeyD') keysRef.current.right = true;
      if (e.code === 'ArrowUp' || e.code === 'KeyW' || e.code === 'Space') keysRef.current.jump = true;
    };
    
    const handleKeyUp = (e) => {
      if (e.code === 'ArrowLeft' || e.code === 'KeyA') keysRef.current.left = false;
      if (e.code === 'ArrowRight' || e.code === 'KeyD') keysRef.current.right = false;
      if (e.code === 'ArrowUp' || e.code === 'KeyW' || e.code === 'Space') keysRef.current.jump = false;
    };
    
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // Touch controls
  useEffect(() => {
    keysRef.current.left = touchControls.left;
    keysRef.current.right = touchControls.right;
    keysRef.current.jump = touchControls.jump;
  }, [touchControls]);

  // Game loop
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let lastTime = 0;
    
    const gameLoop = (timestamp) => {
      if (isPaused) {
        animationRef.current = requestAnimationFrame(gameLoop);
        return;
      }
      
      const deltaTime = timestamp - lastTime;
      lastTime = timestamp;
      
      const state = gameStateRef.current;
      const keys = keysRef.current;
      const level = levelConfig[currentLevel - 1];
      
      // Update player physics
      if (keys.left) {
        state.player.vx = -MOVE_SPEED;
        state.player.facingRight = false;
      } else if (keys.right) {
        state.player.vx = MOVE_SPEED;
        state.player.facingRight = true;
      } else {
        state.player.vx *= FRICTION;
      }
      
      if (keys.jump && state.player.onGround) {
        state.player.vy = JUMP_STRENGTH;
        state.player.onGround = false;
        sounds.jump();
      }
      
      state.player.vy += GRAVITY;
      state.player.x += state.player.vx;
      state.player.y += state.player.vy;
      
      // Keep player in bounds
      if (state.player.x < 0) state.player.x = 0;
      if (state.player.y > 600) {
        state.player.y = 300;
        state.player.x = 50;
        sounds.hit();
      }
      
      // Platform collision
      state.player.onGround = false;
      state.platforms.forEach(platform => {
        if (
          state.player.x < platform.x + platform.width &&
          state.player.x + PLAYER_WIDTH > platform.x &&
          state.player.y + PLAYER_HEIGHT > platform.y &&
          state.player.y + PLAYER_HEIGHT < platform.y + platform.height + state.player.vy
        ) {
          state.player.y = platform.y - PLAYER_HEIGHT;
          state.player.vy = 0;
          state.player.onGround = true;
        }
      });
      
      // Coin collection
      state.coins.forEach(coin => {
        if (!coin.collected) {
          const dx = (state.player.x + PLAYER_WIDTH / 2) - (coin.x + coin.width / 2);
          const dy = (state.player.y + PLAYER_HEIGHT / 2) - (coin.y + coin.height / 2);
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance < 35) {
            coin.collected = true;
            state.collectedCoins++;
            sounds.coin();
          }
        }
        coin.rotation += 0.1;
      });
      
      // Info block collision
      state.infoBlocks.forEach(block => {
        if (!block.triggered) {
          if (
            state.player.x < block.x + block.width &&
            state.player.x + PLAYER_WIDTH > block.x &&
            state.player.y < block.y + block.height &&
            state.player.y + PLAYER_HEIGHT > block.y
          ) {
            block.triggered = true;
            sounds.levelUp();
            onCollectItem(block.data, level.theme);
          }
        }
        block.bounce = Math.sin(timestamp / 200) * 5;
      });
      
      // NPC interaction
      state.npcs.forEach(npc => {
        const dx = (state.player.x + PLAYER_WIDTH / 2) - (npc.x + npc.width / 2);
        const dy = (state.player.y + PLAYER_HEIGHT / 2) - (npc.y + npc.height / 2);
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance < 60 && !npc.talked) {
          npc.talked = true;
          onCollectItem({ type: 'npc', fact: npc.fact }, 'npc');
        }
        npc.bobOffset += 0.05;
      });
      
      // Camera follow
      const targetCameraX = state.player.x - canvas.width / 3;
      state.cameraX += (targetCameraX - state.cameraX) * 0.1;
      if (state.cameraX < 0) state.cameraX = 0;
      
      // Check level completion
      if (state.player.x > 3200) {
        sounds.levelUp();
        onLevelComplete();
      }
      
      // RENDER
      ctx.save();
      
      // Background gradient
      const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
      const colors = level.gradient.match(/#[a-fA-F0-9]{6}/g);
      if (colors) {
        gradient.addColorStop(0, colors[0]);
        gradient.addColorStop(1, colors[1] || colors[0]);
      }
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Parallax clouds
      for (let i = 0; i < 8; i++) {
        ctx.fillStyle = 'rgba(255,255,255,0.4)';
        const cloudX = ((i * 200 - state.cameraX * 0.3) % (canvas.width + 200)) - 100;
        ctx.beginPath();
        ctx.arc(cloudX, 80 + i * 20 % 100, 40, 0, Math.PI * 2);
        ctx.arc(cloudX + 30, 80 + i * 20 % 100 - 10, 30, 0, Math.PI * 2);
        ctx.arc(cloudX + 50, 80 + i * 20 % 100, 35, 0, Math.PI * 2);
        ctx.fill();
      }
      
      ctx.translate(-state.cameraX, 0);
      
      // Platforms (brick-style)
      state.platforms.forEach(platform => {
        // Platform shadow
        ctx.fillStyle = 'rgba(0,0,0,0.3)';
        ctx.fillRect(platform.x + 4, platform.y + 4, platform.width, platform.height);
        
        // Platform body (brick pattern)
        ctx.fillStyle = '#8b4513';
        ctx.fillRect(platform.x, platform.y, platform.width, platform.height);
        
        // Brick lines
        ctx.strokeStyle = '#654321';
        ctx.lineWidth = 2;
        for (let x = platform.x; x < platform.x + platform.width; x += 40) {
          ctx.beginPath();
          ctx.moveTo(x, platform.y);
          ctx.lineTo(x, platform.y + platform.height);
          ctx.stroke();
        }
        for (let y = platform.y; y < platform.y + platform.height; y += 20) {
          ctx.beginPath();
          ctx.moveTo(platform.x, y);
          ctx.lineTo(platform.x + platform.width, y);
          ctx.stroke();
        }
        
        // Platform highlight
        ctx.fillStyle = 'rgba(255,255,255,0.2)';
        ctx.fillRect(platform.x, platform.y, platform.width, 3);
      });
      
      // Coins
      state.coins.forEach(coin => {
        if (!coin.collected) {
          ctx.save();
          ctx.translate(coin.x + coin.width / 2, coin.y + coin.height / 2);
          ctx.scale(Math.cos(coin.rotation), 1);
          
          // Coin glow
          ctx.shadowColor = '#facc15';
          ctx.shadowBlur = 15;
          
          // Coin body
          ctx.fillStyle = '#facc15';
          ctx.beginPath();
          ctx.arc(0, 0, coin.width / 2, 0, Math.PI * 2);
          ctx.fill();
          
          // Coin highlight
          ctx.fillStyle = '#fef08a';
          ctx.beginPath();
          ctx.arc(-3, -3, coin.width / 4, 0, Math.PI * 2);
          ctx.fill();
          
          ctx.restore();
        }
      });
      
      // Info blocks
      state.infoBlocks.forEach(block => {
        if (!block.triggered) {
          ctx.save();
          ctx.translate(block.x, block.y + block.bounce);
          
          // Block glow
          ctx.shadowColor = '#38bdf8';
          ctx.shadowBlur = 20;
          
          // Block body
          ctx.fillStyle = '#38bdf8';
          ctx.fillRect(0, 0, block.width, block.height);
          
          // Question mark
          ctx.fillStyle = '#fff';
          ctx.font = 'bold 28px Fredoka';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText('?', block.width / 2, block.height / 2);
          
          // Border
          ctx.strokeStyle = '#0ea5e9';
          ctx.lineWidth = 3;
          ctx.strokeRect(0, 0, block.width, block.height);
          
          ctx.restore();
        }
      });
      
      // NPCs
      state.npcs.forEach((npc, index) => {
        const bobY = Math.sin(npc.bobOffset) * 3;
        const level = levelConfig[currentLevel - 1];
        
        // Different NPC appearances based on level theme
        if (level.theme === 'education') {
          // Professor/Teacher NPC - wears glasses and mortarboard
          ctx.fillStyle = '#1e40af'; // Blue robe
          ctx.fillRect(npc.x, npc.y + bobY + 15, npc.width, npc.height - 15);
          
          ctx.fillStyle = '#d2b48c'; // Skin
          ctx.beginPath();
          ctx.arc(npc.x + npc.width / 2, npc.y + bobY + 12, 12, 0, Math.PI * 2);
          ctx.fill();
          
          // Glasses
          ctx.strokeStyle = '#000';
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.arc(npc.x + npc.width / 2 - 4, npc.y + bobY + 10, 3, 0, Math.PI * 2);
          ctx.stroke();
          ctx.beginPath();
          ctx.arc(npc.x + npc.width / 2 + 4, npc.y + bobY + 10, 3, 0, Math.PI * 2);
          ctx.stroke();
          ctx.beginPath();
          ctx.moveTo(npc.x + npc.width / 2 - 1, npc.y + bobY + 10);
          ctx.lineTo(npc.x + npc.width / 2 + 1, npc.y + bobY + 10);
          ctx.stroke();
          
          // Mortarboard hat
          ctx.fillStyle = '#000';
          ctx.fillRect(npc.x + 8, npc.y + bobY, 24, 8);
          ctx.fillRect(npc.x + 12, npc.y + bobY - 4, 16, 4);
          
        } else if (level.theme === 'skills') {
          // Wizard/Tech NPC - pointy hat, staff
          ctx.fillStyle = '#4a5568'; // Gray robe
          ctx.fillRect(npc.x, npc.y + bobY + 15, npc.width, npc.height - 15);
          
          ctx.fillStyle = '#fbb6ce'; // Light skin
          ctx.beginPath();
          ctx.arc(npc.x + npc.width / 2, npc.y + bobY + 12, 12, 0, Math.PI * 2);
          ctx.fill();
          
          // Pointy wizard hat
          ctx.fillStyle = '#7c3aed';
          ctx.beginPath();
          ctx.moveTo(npc.x + npc.width / 2, npc.y + bobY - 5);
          ctx.lineTo(npc.x + 8, npc.y + bobY + 8);
          ctx.lineTo(npc.x + 32, npc.y + bobY + 8);
          ctx.closePath();
          ctx.fill();
          
          // Star on hat
          ctx.fillStyle = '#fbbf24';
          ctx.beginPath();
          ctx.arc(npc.x + npc.width / 2, npc.y + bobY - 2, 2, 0, Math.PI * 2);
          ctx.fill();
          
          // Staff
          ctx.strokeStyle = '#92400e';
          ctx.lineWidth = 3;
          ctx.beginPath();
          ctx.moveTo(npc.x + npc.width / 2, npc.y + bobY + 15);
          ctx.lineTo(npc.x + npc.width / 2, npc.y + bobY + 45);
          ctx.stroke();
          
        } else if (level.theme === 'experience') {
          // Businessman NPC - suit and tie
          ctx.fillStyle = '#1e40af'; // Blue suit
          ctx.fillRect(npc.x, npc.y + bobY + 15, npc.width, npc.height - 15);
          
          ctx.fillStyle = '#fed7aa'; // Skin
          ctx.beginPath();
          ctx.arc(npc.x + npc.width / 2, npc.y + bobY + 12, 12, 0, Math.PI * 2);
          ctx.fill();
          
          // Tie
          ctx.fillStyle = '#dc2626';
          ctx.beginPath();
          ctx.moveTo(npc.x + npc.width / 2, npc.y + bobY + 15);
          ctx.lineTo(npc.x + npc.width / 2 - 3, npc.y + bobY + 25);
          ctx.lineTo(npc.x + npc.width / 2 + 3, npc.y + bobY + 25);
          ctx.closePath();
          ctx.fill();
          
          // Briefcase
          ctx.fillStyle = '#92400e';
          ctx.fillRect(npc.x + 5, npc.y + bobY + 35, 12, 8);
          ctx.fillRect(npc.x + 8, npc.y + bobY + 33, 6, 2);
          
        } else if (level.theme === 'projects') {
          // Inventor NPC - goggles, tool belt
          ctx.fillStyle = '#059669'; // Green overalls
          ctx.fillRect(npc.x, npc.y + bobY + 15, npc.width, npc.height - 15);
          
          ctx.fillStyle = '#fed7aa'; // Skin
          ctx.beginPath();
          ctx.arc(npc.x + npc.width / 2, npc.y + bobY + 12, 12, 0, Math.PI * 2);
          ctx.fill();
          
          // Goggles
          ctx.strokeStyle = '#000';
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.arc(npc.x + npc.width / 2 - 3, npc.y + bobY + 8, 4, 0, Math.PI * 2);
          ctx.stroke();
          ctx.beginPath();
          ctx.arc(npc.x + npc.width / 2 + 3, npc.y + bobY + 8, 4, 0, Math.PI * 2);
          ctx.stroke();
          ctx.beginPath();
          ctx.moveTo(npc.x + npc.width / 2 - 3, npc.y + bobY + 8);
          ctx.lineTo(npc.x + npc.width / 2 + 3, npc.y + bobY + 8);
          ctx.stroke();
          
          // Wrench/tool
          ctx.fillStyle = '#6b7280';
          ctx.fillRect(npc.x + 15, npc.y + bobY + 35, 8, 3);
          ctx.fillRect(npc.x + 19, npc.y + bobY + 32, 3, 8);
          
        } else if (level.theme === 'contact') {
          // Friendly contact NPC - smiling, waving
          ctx.fillStyle = '#f59e0b'; // Yellow shirt
          ctx.fillRect(npc.x, npc.y + bobY + 15, npc.width, npc.height - 15);
          
          ctx.fillStyle = '#fbbf24'; // Skin
          ctx.beginPath();
          ctx.arc(npc.x + npc.width / 2, npc.y + bobY + 12, 12, 0, Math.PI * 2);
          ctx.fill();
          
          // Smile
          ctx.strokeStyle = '#000';
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.arc(npc.x + npc.width / 2, npc.y + bobY + 15, 3, 0, Math.PI);
          ctx.stroke();
          
          // Waving hand
          ctx.fillStyle = '#fed7aa';
          ctx.beginPath();
          ctx.arc(npc.x + npc.width / 2 + 15, npc.y + bobY + 20, 4, 0, Math.PI * 2);
          ctx.fill();
          
        } else {
          // Default Goomba-style for any other theme
          ctx.fillStyle = '#1e40af';
          ctx.fillRect(npc.x, npc.y + bobY + 15, npc.width, npc.height - 15);
          
          ctx.fillStyle = '#d2b48c';
          ctx.beginPath();
          ctx.arc(npc.x + npc.width / 2, npc.y + bobY + 12, 12, 0, Math.PI * 2);
          ctx.fill();
          
          ctx.fillStyle = '#000';
          ctx.beginPath();
          ctx.arc(npc.x + npc.width / 2 - 4, npc.y + bobY + 10, 2, 0, Math.PI * 2);
          ctx.fill();
          ctx.beginPath();
          ctx.arc(npc.x + npc.width / 2 + 4, npc.y + bobY + 10, 2, 0, Math.PI * 2);
          ctx.fill();
          
          ctx.fillStyle = '#654321';
          ctx.fillRect(npc.x + 5, npc.y + bobY + 35, 8, 5);
          ctx.fillRect(npc.x + 17, npc.y + bobY + 35, 8, 5);
        }
        
        // Speech indicator (same for all)
        if (!npc.talked) {
          ctx.fillStyle = 'rgba(255,255,255,0.9)';
          ctx.beginPath();
          ctx.arc(npc.x + npc.width / 2, npc.y - 20 + bobY, 15, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = '#1e293b';
          ctx.font = 'bold 16px Fredoka';
          ctx.textAlign = 'center';
          ctx.fillText('!', npc.x + npc.width / 2, npc.y - 16 + bobY);
        }
      });
      
      // Player (Mario-style)
      ctx.save();
      ctx.translate(state.player.x + PLAYER_WIDTH / 2, state.player.y + PLAYER_HEIGHT / 2);
      if (!state.player.facingRight) ctx.scale(-1, 1);
      ctx.translate(-PLAYER_WIDTH / 2, -PLAYER_HEIGHT / 2);
      
      // Mario hat (red)
      ctx.fillStyle = '#dc2626';
      ctx.fillRect(8, 0, 24, 12);
      ctx.fillRect(6, 8, 28, 6);
      
      // Mario face (peach)
      ctx.fillStyle = '#fbbf24';
      ctx.fillRect(10, 12, 20, 16);
      
      // Mario eyes
      ctx.fillStyle = '#1e293b';
      ctx.fillRect(14, 16, 3, 3);
      ctx.fillRect(23, 16, 3, 3);
      
      // Mario mustache
      ctx.fillStyle = '#92400e';
      ctx.fillRect(16, 22, 8, 2);
      
      // Mario overalls (blue)
      ctx.fillStyle = '#1d4ed8';
      ctx.fillRect(8, 28, 24, 18);
      ctx.fillRect(6, 32, 28, 8);
      
      // Mario buttons
      ctx.fillStyle = '#fbbf24';
      ctx.fillRect(18, 34, 4, 4);
      
      // Mario shoes (brown)
      ctx.fillStyle = '#92400e';
      ctx.fillRect(6, 44, 12, 6);
      ctx.fillRect(22, 44, 12, 6);
      
      ctx.restore();
      
      // Level end flag
      ctx.fillStyle = '#22c55e';
      ctx.fillRect(3200, 350, 10, 100);
      ctx.fillStyle = '#ef4444';
      ctx.fillRect(3210, 350, 40, 30);
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 12px Fredoka';
      ctx.fillText('END', 3215, 370);
      
      ctx.restore();
      
      animationRef.current = requestAnimationFrame(gameLoop);
    };
    
    animationRef.current = requestAnimationFrame(gameLoop);
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [currentLevel, isPaused, onCollectItem, onLevelComplete]);

  const handleTouchStart = (control) => {
    setTouchControls(prev => ({ ...prev, [control]: true }));
  };

  const handleTouchEnd = (control) => {
    setTouchControls(prev => ({ ...prev, [control]: false }));
  };

  return (
    <div className="relative w-full h-full" data-testid="game-canvas-container">
      <canvas
        ref={canvasRef}
        width={800}
        height={500}
        className="w-full h-auto border-4 border-slate-800 rounded-xl shadow-2xl max-w-full"
        style={{ maxHeight: isMobile ? '60vh' : '500px' }}
        data-testid="game-canvas"
      />
      
      {/* Mobile Touch Controls */}
      {isMobile && (
        <div className="mobile-touch-controls" data-testid="touch-controls">
          <div className="mobile-direction-controls">
            <button
              className="mobile-control-btn"
              onTouchStart={() => handleTouchStart('left')}
              onTouchEnd={() => handleTouchEnd('left')}
              onMouseDown={() => handleTouchStart('left')}
              onMouseUp={() => handleTouchEnd('left')}
              onMouseLeave={() => handleTouchEnd('left')}
              data-testid="touch-left"
            >
              ←
            </button>
            <button
              className="mobile-control-btn"
              onTouchStart={() => handleTouchStart('right')}
              onTouchEnd={() => handleTouchEnd('right')}
              onMouseDown={() => handleTouchStart('right')}
              onMouseUp={() => handleTouchEnd('right')}
              onMouseLeave={() => handleTouchEnd('right')}
              data-testid="touch-right"
            >
              →
            </button>
          </div>
          <button
            className="mobile-control-btn mobile-jump-btn"
            onTouchStart={() => handleTouchStart('jump')}
            onTouchEnd={() => handleTouchEnd('jump')}
            onMouseDown={() => handleTouchStart('jump')}
            onMouseUp={() => handleTouchEnd('jump')}
            onMouseLeave={() => handleTouchEnd('jump')}
            data-testid="touch-jump"
          >
            JUMP
          </button>
        </div>
      )}
    </div>
  );
};

export default GameCanvas;
